import { onUnmounted, watch } from 'vue';
import { alertController, useIonRouter } from '@ionic/vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/stores/main';

/** Inactivity is local. Connectivity/auth checks belong to the tab-return checker. */
export function useSimpleSessionManager() {
  const store = useStore();
  const router = useIonRouter();
  const { locale } = useI18n();
  let expiryTimer: ReturnType<typeof setTimeout> | null = null;
  let warningTimer: ReturnType<typeof setTimeout> | null = null;
  let warning: HTMLIonAlertElement | null = null;
  let warningShown = false;
  let lastReset = 0;
  const warningTime = 15 * 60 * 1000;

  function clearTimers() {
    if (expiryTimer) clearTimeout(expiryTimer);
    if (warningTimer) clearTimeout(warningTimer);
    expiryTimer = warningTimer = null;
  }

  function expire() {
    if (!store.loggedIn) return;
    if (!store.isSessionExpired()) return startSessionTimer();
    signOut();
  }

  function signOut() {
    clearTimers();
    // logout clears local auth synchronously; its remote revocation is bounded.
    void store.logout();
    void router.replace('/login');
  }

  async function showWarning() {
    if (warningShown || document.hidden || !store.loggedIn) return;
    warningShown = true;
    const sv = locale.value === 'sv';
    warning = await alertController.create({
      header: sv ? 'Sessionen håller på att gå ut' : 'Your session is about to expire',
      message: sv ? 'Fortsätt arbeta för att stanna inloggad.' : 'Continue working to stay signed in.',
      buttons: [
        { text: sv ? 'Logga ut' : 'Sign out', handler: signOut },
        { text: sv ? 'Fortsätt arbeta' : 'Continue working', handler: extendSession },
      ],
    });
    if (store.loggedIn && !document.hidden) await warning.present();
  }

  function startSessionTimer() {
    clearTimers();
    if (!store.loggedIn) return;
    const remaining = store.getTimeUntilExpiry();
    expiryTimer = setTimeout(expire, remaining);
    if (remaining > warningTime) warningTimer = setTimeout(() => void showWarning(), remaining - warningTime);
  }

  function extendSession() {
    if (!store.loggedIn) return;
    store.updateLastActivity();
    warningShown = false;
    startSessionTimer();
  }

  function resetSession() {
    if (document.hidden || Date.now() - lastReset < 60000) return;
    lastReset = Date.now();
    extendSession();
  }

  function handleReturn() {
    if (document.hidden || !store.loggedIn) return;
    if (store.isSessionExpired()) expire();
    else {
      startSessionTimer();
      if (store.getTimeUntilExpiry() <= warningTime) void showWarning();
    }
  }

  const events = ['pointerdown', 'keydown', 'touchstart'];
  events.forEach((event) => document.addEventListener(event, resetSession, { passive: true }));
  document.addEventListener('visibilitychange', handleReturn);
  const stopWatch = watch(() => store.loggedIn, () => {
    warningShown = false;
    if (!store.loggedIn) void warning?.dismiss();
    startSessionTimer();
  }, { immediate: true });

  function cleanup() {
    clearTimers();
    stopWatch();
    void warning?.dismiss();
    events.forEach((event) => document.removeEventListener(event, resetSession));
    document.removeEventListener('visibilitychange', handleReturn);
  }
  onUnmounted(cleanup);
  return { startSessionTimer, extendSession, resetSession, cleanup };
}
